/**
 * Combined Location and Remoteness Distribution Chart
 * Shows both distributions side by side in a single component
 */

import React, { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { StarburstChart } from './CompanyCharts';
import { DonutSunburstChart } from './DonutSunburstChart';
import { hasAnyValidNumber } from '../utils/numberUtils';
import { Msg, useFormatMessage } from '../../../../../i18n-review/client/Msg';

interface LocationRemotenessChartProps {
  locationDistribution?: Record<string, number>;
  remotenessDistribution?: { remote: number; hybrid: number; onsite: number };
  brandColour?: string;
  companyName?: string;
  locale?: string;
  className?: string;
}

export const LocationRemotenessChart: React.FC<LocationRemotenessChartProps> = memo(({
  locationDistribution,
  remotenessDistribution,
  brandColour,
  companyName,
  locale = 'en',
  className = '',
}) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  
  const hasLocationData = locationDistribution &&
    Object.keys(locationDistribution).length > 0 &&
    Object.values(locationDistribution).some(val => hasAnyValidNumber(val));
  
  const hasRemotenessData = remotenessDistribution &&
    (remotenessDistribution.remote > 0 ||
     remotenessDistribution.hybrid > 0 ||
     remotenessDistribution.onsite > 0);
  
  // Don't render if neither has data
  if (!hasLocationData && !hasRemotenessData) {
    return null;
  }
  
  // Determine which dimension has more values (goes on outside donut)
  // The dimension with MORE values goes on the outside, fewer values on the inside
  const locationCount = hasLocationData ? Object.keys(locationDistribution!).length : 0;
  const remotenessCount = hasRemotenessData ? 
    [remotenessDistribution!.remote, remotenessDistribution!.hybrid, remotenessDistribution!.onsite].filter(v => v > 0).length : 0;
  
  const useCombinedChart = hasLocationData && hasRemotenessData && locationCount > 0 && remotenessCount > 0;
  // Location typically has more values, so it goes on the outside
  const locationOnOutside = locationCount >= remotenessCount;
  
  // Generate outer caption (for the donut)
  const generateOuterCaption = () => {
    if (!companyName) return null;
    
    if (locationOnOutside && hasLocationData && locationDistribution) {
      const locationEntries = Object.entries(locationDistribution)
        .map(([label, value]) => [label, value] as [string, number])
        .filter(([, value]) => value > 0)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3);
      
      if (locationEntries.length === 1) {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#location-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Location Distribution:</a>{' '}
            <Msg
              id="companyChart.caption.locationDistributionSingle"
              defaultMessage="{companyName} hires mostly in {location}."
              values={{ companyName, location: locationEntries[0][0] }}
            />
          </p>
        );
      } else if (locationEntries.length === 2) {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#location-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Location Distribution:</a>{' '}
            <Msg
              id="companyChart.caption.locationDistributionDouble"
              defaultMessage="{companyName} hires mostly in {location1} and {location2}."
              values={{ companyName, location1: locationEntries[0][0], location2: locationEntries[1][0] }}
            />
          </p>
        );
      } else if (locationEntries.length >= 3) {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#location-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Location Distribution:</a>{' '}
            <Msg
              id="companyChart.caption.locationDistributionTriple"
              defaultMessage="{companyName} hires mostly in {location1}, {location2}, and {location3}."
              values={{ companyName, location1: locationEntries[0][0], location2: locationEntries[1][0], location3: locationEntries[2][0] }}
            />
          </p>
        );
      }
    } else if (!locationOnOutside && hasRemotenessData && remotenessDistribution) {
      const remoteCount = remotenessDistribution.remote || 0;
      const hybridCount = remotenessDistribution.hybrid || 0;
      const onsiteCount = remotenessDistribution.onsite || 0;
      
      const maxType = remoteCount > hybridCount && remoteCount > onsiteCount ? 'remote'
        : hybridCount > onsiteCount ? 'hybrid'
        : 'onsite';
      
      if (maxType === 'remote') {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#remoteness-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Remoteness Distribution:</a>{' '}
            <Msg
              id="companyChart.caption.remotenessRemote"
              defaultMessage="{companyName} hires mostly remote positions."
              values={{ companyName }}
            />
          </p>
        );
      } else if (maxType === 'hybrid') {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#remoteness-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Remoteness Distribution:</a>{' '}
            <Msg
              id="companyChart.caption.remotenessHybrid"
              defaultMessage="{companyName} hires mostly hybrid positions."
              values={{ companyName }}
            />
          </p>
        );
      } else {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#remoteness-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Remoteness Distribution:</a>{' '}
            <Msg
              id="companyChart.caption.remotenessOnsite"
              defaultMessage="{companyName} hires mostly onsite positions."
              values={{ companyName }}
            />
          </p>
        );
      }
    }
    return null;
  };
  
  // Generate inner caption (for the pill)
  const generateInnerCaption = () => {
    if (!companyName) return null;
    
    if (locationOnOutside && hasRemotenessData && remotenessDistribution) {
      const remoteCount = remotenessDistribution.remote || 0;
      const hybridCount = remotenessDistribution.hybrid || 0;
      const onsiteCount = remotenessDistribution.onsite || 0;
      
      const maxType = remoteCount > hybridCount && remoteCount > onsiteCount ? 'remote'
        : hybridCount > onsiteCount ? 'hybrid'
        : 'onsite';
      
      if (maxType === 'remote') {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#remoteness-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Remoteness Distribution:</a>{' '}
            <Msg
              id="companyChart.caption.remotenessRemote"
              defaultMessage="{companyName} hires mostly remote positions."
              values={{ companyName }}
            />
          </p>
        );
      } else if (maxType === 'hybrid') {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#remoteness-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Remoteness Distribution:</a>{' '}
            <Msg
              id="companyChart.caption.remotenessHybrid"
              defaultMessage="{companyName} hires mostly hybrid positions."
              values={{ companyName }}
            />
          </p>
        );
      } else {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#remoteness-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Remoteness Distribution:</a>{' '}
            <Msg
              id="companyChart.caption.remotenessOnsite"
              defaultMessage="{companyName} hires mostly onsite positions."
              values={{ companyName }}
            />
          </p>
        );
      }
    } else if (!locationOnOutside && hasLocationData && locationDistribution) {
      const locationEntries = Object.entries(locationDistribution)
        .map(([label, value]) => [label, value] as [string, number])
        .filter(([, value]) => value > 0)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3);
      
      if (locationEntries.length === 1) {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#location-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Location Distribution:</a>{' '}
            <Msg
              id="companyChart.caption.locationDistributionSingle"
              defaultMessage="{companyName} hires mostly in {location}."
              values={{ companyName, location: locationEntries[0][0] }}
            />
          </p>
        );
      } else if (locationEntries.length === 2) {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#location-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Location Distribution:</a>{' '}
            <Msg
              id="companyChart.caption.locationDistributionDouble"
              defaultMessage="{companyName} hires mostly in {location1} and {location2}."
              values={{ companyName, location1: locationEntries[0][0], location2: locationEntries[1][0] }}
            />
          </p>
        );
      } else if (locationEntries.length >= 3) {
        return (
          <p className="text-xs font-normal text-jc-muted leading-relaxed m-0">
            <a href="#location-distribution" className="font-normal text-jc-muted hover:text-jc-ink-secondary underline-offset-2 hover:underline">Location Distribution:</a>{' '}
            <Msg
              id="companyChart.caption.locationDistributionTriple"
              defaultMessage="{companyName} hires mostly in {location1}, {location2}, and {location3}."
              values={{ companyName, location1: locationEntries[0][0], location2: locationEntries[1][0], location3: locationEntries[2][0] }}
            />
          </p>
        );
      }
    }
    return null;
  };
  
  const outerCaption = generateOuterCaption();
  const innerCaption = generateInnerCaption();
  
  return (
    <div className={`location-remoteness-chart ${className} flex flex-col items-center`}>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2 w-full">
        <Msg
          id="jobDetailPage.locationAndRemotenessDistribution"
          defaultMessage="Location & Remoteness Distribution"
          description="Chart title for combined location and remoteness distribution visualization"
        />
      </h3>
      
      {useCombinedChart ? (
        // Combined donut/sunburst chart
        // More values go on outside (donut), fewer values go on inside (sunburst)
        <div className="w-full">
          <DonutSunburstChart
            outerData={locationOnOutside ? locationDistribution! : {
              Remote: remotenessDistribution!.remote || 0,
              Hybrid: remotenessDistribution!.hybrid || 0,
              Onsite: remotenessDistribution!.onsite || 0,
            }}
            innerData={locationOnOutside ? {
              Remote: remotenessDistribution!.remote || 0,
              Hybrid: remotenessDistribution!.hybrid || 0,
              Onsite: remotenessDistribution!.onsite || 0,
            } : locationDistribution!}
            outerTitle={locationOnOutside 
              ? formatMessage({ id: "jobDetailPage.locationDistribution", defaultMessage: "Location Distribution", description: "Chart title for location distribution showing where company posts jobs" })
              : formatMessage({ id: "jobDetailPage.remotenessDistribution", defaultMessage: "Remoteness Distribution", description: "Chart title for remoteness distribution showing work model breakdown" })}
            innerTitle={locationOnOutside
              ? formatMessage({ id: "jobDetailPage.remotenessDistribution", defaultMessage: "Remoteness Distribution", description: "Chart title for remoteness distribution showing work model breakdown" })
              : formatMessage({ id: "jobDetailPage.locationDistribution", defaultMessage: "Location Distribution", description: "Chart title for location distribution showing where company posts jobs" })}
            brandColour={brandColour}
            companyName={companyName}
            locale={locale}
            maxInnerItems={12}
            outerCaption={outerCaption}
            innerCaption={innerCaption}
          />
        </div>
      ) : (
        // Fallback to side-by-side charts if we don't have both
        <div className="flex flex-col md:flex-row gap-6 items-start justify-center w-full">
          {hasLocationData && (
            <div className="flex-1 min-w-0">
              <StarburstChart
                data={locationDistribution!}
                title={formatMessage({ id: "jobDetailPage.locationDistribution", defaultMessage: "Location Distribution", description: "Chart title for location distribution showing where company posts jobs" })}
                titleKey="locationDistribution"
                brandColour={brandColour}
                companyName={companyName}
                locale={locale}
                maxItems={6}
              />
            </div>
          )}
          {hasRemotenessData && (
            <div className="flex-1 min-w-0">
              <StarburstChart
                data={{
                  Remote: remotenessDistribution!.remote || 0,
                  Hybrid: remotenessDistribution!.hybrid || 0,
                  Onsite: remotenessDistribution!.onsite || 0,
                }}
                title={formatMessage({ id: "jobDetailPage.remotenessDistribution", defaultMessage: "Remoteness Distribution", description: "Chart title for remoteness distribution showing work model breakdown" })}
                titleKey="remotenessDistribution"
                brandColour={brandColour}
                companyName={companyName}
                locale={locale}
                maxItems={3}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
});

LocationRemotenessChart.displayName = 'LocationRemotenessChart';

